import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle, ShoppingBag, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useShop } from '../shop/ShopContext';

/**
 * Landing page dopo confirmPayment di Stripe (Express Checkout).
 * Query params dopo il redirect:
 *   ?pi=<payment_intent_id>&crafter_id=<...>&redirect_status=succeeded|processing|failed
 * (i primi due li passiamo noi in confirmParams.return_url, gli altri li aggiunge Stripe)
 */
export default function OrderSuccessPage() {
  const shop = useShop();
  const [params] = useSearchParams();
  const pi = params.get('pi');
  const stripeStatus = params.get('redirect_status');
  const orderParam = params.get('orders');
  const orderNumbers = orderParam ? orderParam.split(',').filter(Boolean) : [];
  const [state, setState] = useState('loading'); // loading | success | processing | failed

  useEffect(() => {
    if (!pi) {
      setState('failed');
      return;
    }
    // Fast path: se Stripe ci dice succeeded, saltiamo la verifica server-side
    if (stripeStatus === 'succeeded') {
      setState('success');
      // Svuota carrello (pagamento andato a buon fine)
      try {
        localStorage.removeItem('rarifex_cart');
        localStorage.removeItem('rarifex_pending_cart');
        window.dispatchEvent(new Event('cartUpdated'));
      } catch (_) { /* no-op */ }
      return;
    }
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/checkout/payment-intent-status/${pi}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.status === 'succeeded' || d.status === 'processing' || d.status === 'requires_capture') {
          setState(d.status === 'succeeded' ? 'success' : 'processing');
          // Svuota carrello anche quando il pagamento è ancora in processing:
          // i fondi sono già impegnati, il cliente non deve poter ripagare.
          try {
            localStorage.removeItem('rarifex_cart');
            localStorage.removeItem('rarifex_pending_cart');
            window.dispatchEvent(new Event('cartUpdated'));
          } catch (_) { /* no-op */ }
        } else {
          setState('failed');
        }
      })
      .catch(() => setState('failed'));
  }, [pi, stripeStatus]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 mx-auto text-shop-primary animate-spin mb-3" />
          <p className="text-slate-600">Verifica del pagamento in corso...</p>
        </div>
      </div>
    );
  }

  if (state === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md bg-white rounded-2xl border p-8 text-center shadow-sm">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-semibold mb-2">Pagamento non riuscito</h1>
          <p className="text-slate-600 mb-6">
            La transazione non è andata a buon fine. Puoi riprovare dal tuo carrello.
          </p>
          <Button asChild className="bg-shop-primary hover:bg-shop-primary/90 text-white">
            <Link to="/cart">Torna al carrello</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isProcessing = state === 'processing';

  return (
    <div className={`min-h-screen py-12 px-4 ${shop ? '' : 'bg-gradient-to-b from-teal-50/40 to-white'}`}>
      <div className="max-w-xl mx-auto text-center" data-testid="order-success-page">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
          <CheckCircle2 className="w-9 h-9" />
        </div>
        <h1 className="text-3xl font-bold text-slate-800 mb-3">
          {isProcessing ? 'Pagamento in elaborazione' : 'Grazie per il tuo ordine!'}
        </h1>
        <p className="text-base text-slate-600 mb-8">
          {isProcessing
            ? 'Il tuo pagamento è stato ricevuto ed è in fase di conferma. Riceverai una email quando l\'artigiano avrà preso in carico l\'ordine.'
            : 'Il pagamento è andato a buon fine. L\'artigiano è stato avvisato e riceverai a breve una email di conferma con i dettagli dell\'ordine.'}
        </p>

        {orderNumbers.length > 0 && (
          <div className="bg-white border rounded-xl p-5 mb-8 text-left shadow-sm" data-testid="order-success-numbers">
            <p className="text-sm text-slate-500 mb-1">
              {orderNumbers.length > 1 ? 'I tuoi numeri d\'ordine' : 'Il tuo numero d\'ordine'}
            </p>
            <div className="space-y-1">
              {orderNumbers.map((n) => (
                <p key={n} className="font-mono font-bold text-slate-800" data-testid="order-success-number">{n}</p>
              ))}
            </div>
            {orderNumbers.length > 1 && (
              <p className="text-xs text-slate-500 mt-3">
                Abbiamo creato un ordine separato per ogni artigiano: così ognuno potrà preparare con cura la sua creazione. Trovi tutti i dettagli nella sezione “I miei ordini”.
              </p>
            )}
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild className="bg-shop-primary hover:bg-shop-primary/90 text-white">
            <Link to="/dashboard?tab=cliente&section=active-orders" data-testid="cta-my-orders">
              <ShoppingBag className="w-4 h-4 mr-2" /> I miei ordini
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={shop ? shop.paths.home : '/'} data-testid="cta-home">
              <Home className="w-4 h-4 mr-2" /> {shop ? `Torna a ${shop.name}` : 'Torna alla home'}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
